/* eslint-disable max-len */
import { DownloadOutlined } from '@ant-design/icons';
import {
    message,
    DatePicker,
} from 'antd';
import { ColumnProps } from 'antd/lib/table';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import { sortBy } from 'lodash';
import moment, { Moment } from 'moment';
import { getInvoiceDownloadRequests } from 'network/consignments.api';
import * as React from 'react';
import { ReduxStore } from 'reducers/redux.types';
import { Master } from 'types/master-data-types';
import { getDownloadRequests, getPickupDownloadRequests } from '../../network/common.api';
import { StylesProps, ThemeType } from '../../theme/jss-types';
import { useTranslation } from 'react-i18next';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';

const downloadPageStyles = (theme : ThemeType) => ({
    main: {
        width: '100%',
        padding: '5px 6px 6px 3px',
        height: '100%',
    },
    title: {
        width: '100%',
        backgroundColor: '#FFFFFF',
        padding: '8px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: 4,
        fontSize: 16,
        color: '#111111',
        fontWeight: 'bold',
        alignItems: 'center',
    },
    dateFilter: {
        backgroundColor: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        '& .ant-picker': {
            borderRadius: '6px',
            width: 215,
            height: 32,
        },
        '& .ant-picker-input > input': {
            fontSize: '13px',
        },
    },
    dateRange: {
        '& .ant-picker-cell-in-view::after': {
            display: 'none !important',
            content: 'none !important',
        },
        '& .ant-picker-cell:hover .ant-picker-cell-in-view:hover::after': {
            display: 'none !important',
            content: 'none !important',
        },
        '& .ant-picker-panel-container': {
            backgroundColor: '#f7f7f7 !important',
            borderRadius: '4px !important',
            display: 'flex !important',
            flexDirection: 'row-reverse !important',
        },
        '& .ant-picker-cell-in-view .ant-picker-cell-inner': {
            display: 'flex !important',
            alignItems: 'center !important',
            justifyContent: 'center !important',
            width: '32px !important',
            height: '32px !important',
            padding: '0 !important',
            backgroundColor: 'white !important',
            borderRadius: '4px !important',
            margin: '1px !important',
            boxSizing: 'border-box !important',
            '&:hover': {
                backgroundColor: '#b9e0ff !important',
            },
        },
        '& .ant-picker-cell-in-view': {
            padding: '0 !important',
            '&:hover': {
                backgroundColor: 'transparent !important',
            },
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-in-range .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner': {
            backgroundColor: `${theme.tertiaryFillColor} !important`,
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner, & .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner': {
            backgroundColor: `${theme.secondaryFillColor} !important`,
            borderColor: `${theme.secondaryFillColor} !important`,
            fontWeight: 'bold !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-in-range::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single)::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single)::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-cell-disabled::before': {
            background: '#f7f7f7',
        },
        '& .ant-picker-date-panel .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-start .ant-picker-cell-inner::after': {
            background: 'transparent !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover::before': {
            background: 'transparent !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-start:not(.ant-picker-cell-range-start-single).ant-picker-cell-range-hover-start::before': {
            background: 'transparent !important',
        },
        '& .ant-picker-date-panel .ant-picker-cell-in-view.ant-picker-cell-in-range.ant-picker-cell-range-hover-end .ant-picker-cell-inner::after': {
            background: 'transparent !important',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-range-end:not(.ant-picker-cell-range-end-single).ant-picker-cell-range-hover-end::before': {
            background: 'transparent !important',
        },
        '& .ant-picker-footer': {
            minWidth: 140,
            padding: 4,
        },
        '& .ant-picker-ranges .ant-picker-preset > .ant-tag-blue': {
            color: 'black',
            background: 'transparent !important',
            borderColor: 'transparent !important',
            fontWeight: 600,
            fontSize: '12px',
            padding: '8px',
        },
        '& .ant-picker-ranges > li:hover': {
            display: 'inline-block',
            backgroundColor: `${theme.tertiaryFillColor} !important`,
            borderRadius: '4px',
        },
        '& .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner': {
        },
    },
    cellValue: {
        color: '#111111',
        fontFamily: 'Open Sans',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
        wordWrap: 'break-word',
        whiteSpace: 'break-spaces',
    },
    col: {
        display: 'flex',
        flexDirection: 'column',
    },
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
    },
    name: {
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 0,
        color: '#111111',
    },
    value: {
        fontSize: 12,
        letterSpacing: 0,
        color: '#111111',
    },
    middle: {
        margin: '0 8px',
    },
});

const {
    useEffect,
    useState,
} = React;

type Request = {
    createdAt: Moment;
    filters: Record<string, string>;
    status: string;
    type: string;
    url: string;
    dumpType: string;
};

interface DownloadPageProps extends StylesProps<ReturnType<typeof downloadPageStyles>> {
    master: Master,
}

const Downloads = (props: DownloadPageProps) => {
    const {
        classes,
        master,
    } = props;

    const [loading, setLoading] = useState<boolean>(true);
    const [requests, setRequests] = useState<Request[]>([]);
    const [createdAtDate, setCreatedAtDate] = React.useState(moment());
    const allow_child_login_on_parent = master?.parts_to_show?.allow_child_login_on_parent || false;
    const { t, i18n } = useTranslation();

    const loadRequests = async () => {
        const dateFormat = 'YYYY-MM-DD';
        setLoading(true);
        const response = await getDownloadRequests({
            fromDate: moment(createdAtDate).format(dateFormat),
            toDate: moment(createdAtDate).format(dateFormat),
            dumpType: ['CUSTOMER_PORTAL_CONSIGNMENT', 'CUSTOMER_PORTAL_CONSIGNMENT_DTDC', 'CUSTOMER_PORTAL_REPORT', 'PLUGIN_ORDER_DOWNLOAD'],
        });
        const invoiceResponse = await getInvoiceDownloadRequests({
            date: moment().format('MMMM YYYY'),
            dumpType: 'CUSTOMER_PORTAL_INVOICE_REPORTS',
        });
        const pickupRequests = await getPickupDownloadRequests({
            fromDate: moment(createdAtDate).format(dateFormat),
            toDate: moment(createdAtDate).format(dateFormat),
        });
        const requestList = [];

        if (response.isSuccess) {
            requestList.push(...(response?.data?.requests || []));
        } else {
            message.error(response.errorMessage);
        }
        if (pickupRequests.isSuccess) {
            const pickupRequestsList = pickupRequests.data.map((request: any) => {
                return {
                    dumpType: 'Pickups',
                    filterParams: request.filters,
                    createdAt: request.createdAt,
                    status: request.status,
                    url: request.url,
                };
            });
            requestList.push(...(pickupRequestsList || []));
        } else {
            message.error(response.errorMessage);
        }
        if (invoiceResponse.isSuccess) {
            requestList.push(...(invoiceResponse?.data?.requests || []));
        } else {
            message.error(response.errorMessage);
        }
        setRequests(sortBy(requestList, ['createdAt']).reverse());
        setLoading(false);
    };

    useEffect(() => {
        loadRequests();
    }, [createdAtDate]);

    const getDisabled = (current: Moment) => {
        return current && moment(current) >= moment();
    };

    const renderCreatedAtFilter = () => {
        return (
            <div className={classes.dateFilter}>
                <span style={{ fontWeight: 400, fontSize: '14px', marginRight: '10px' }}>{t('createdAt')}</span>
                <DatePicker
                    value={createdAtDate}
                    disabledDate={(current) => getDisabled(current)}
                    placeholder="Select Date"
                    allowClear={false}
                    onChange={(value) => {
                        setCreatedAtDate(moment(value));
                    }}
                    dropdownClassName={classes.dateRange}
                />
            </div>
        );
    };

    const renderTitle = () => {
        return (
            <div className={classes.title}>
                {t('download_request')}
                {allow_child_login_on_parent && renderCreatedAtFilter()}
            </div>
        );
    };

    const downloadColumns: Record<string, string> = {
        serialNumber: 'S.No.',
        dumpType: 'Request Type',
        filterParams: 'Filters',
        createdAt: 'Created At',
        status: 'Status',
        url: 'Link',
    };

    const renderText = (text: string | number) => {
        const data = (text || '').toString().toLowerCase();
        return (
            <div
                className={classes.cellValue}
            >
                {`${i18n.exists(data) ? t(data) : text}`}
            </div>
        );
    };

    const renderFilter = (name: string, value: string) => {
        return (
            <span className={classes.row}>
                <span className={classes.name}>{name}</span>
                <span className={classes.middle}>-</span>
                <span className={classes.value}>{value || 'ALL'}</span>
            </span>
        );
    };

    const renderRemittanceFilters = (filters: Record<string, string>) => {
        const dateFormat = 'YYYY/MM/DD';
        const fromDate = filters?.fromDate?.replaceAll('-', '/') || (filters?.startDate ? moment(filters?.startDate).format(dateFormat) : 'ALL');
        const toDate = filters?.toDate?.replaceAll('-', '/') || (filters?.endDate ? moment(filters?.endDate).format(dateFormat) : 'ALL');
        const dateString = `${fromDate} - ${toDate}`;
        return (
            <div
                className={classes.col}
            >
                {renderFilter('Date', dateString)}
                {renderFilter('Account', filters?.customerCode)}
            </div>
        );
    };

    const renderFilters = (filters: Record<string, string>) => {
        const dateFormat = 'YYYY/MM/DD';
        const fromDate = filters?.fromDate?.replaceAll('-', '/') || (filters?.startDate ? moment(filters?.startDate).format(dateFormat) : 'ALL');
        const toDate = filters?.toDate?.replaceAll('-', '/') || (filters?.endDate ? moment(filters?.endDate).format(dateFormat) : 'ALL');
        const dateString = `${fromDate} - ${toDate}`;
        return (
            <div
                className={classes.col}
            >
                {renderFilter(t('date'), dateString)}
                {renderFilter(t('account'), filters?.clientName)}
                {renderFilter(t('status'), filters?.status)}
                {renderFilter(t('service_type'), filters?.serviceType)}
                {renderFilter(t('consignment_type'), filters?.consignmentType?.toUpperCase())}
            </div>
        );
    };

    const renderInvoiceFilters = (filters: Record<string, string>) => {
        return (
            <div
                className={classes.col}
            >
                {renderFilter('Date', filters?.date)}
                {renderFilter('Account', filters?.account)}
                {renderFilter('Invoice Number', filters?.invoiceNumber)}
                {renderFilter('SBU', filters?.sbu)}
            </div>
        );
    };

    const renderDownload = (url: string) => {
        if (!url) {
            return null;
        }
        return (
            <a href={url} download>
                <DownloadOutlined />
            </a>
        );
    };

    const renderDate = (date: Moment) => {
        const dateString = moment(date).format('DD MMM, YYYY | hh:mm:A');
        return renderText(dateString);
    };

    const renderColumn = (
        text: string | Record<string, string> | Moment,
        row: Request,
        column: string,
        index: number,
    ) => {
        switch (column) {
            case 'filterParams':
                if (row.dumpType === 'CUSTOMER_PORTAL_INVOICE_REPORTS') {
                    return renderInvoiceFilters(text as Record<string, string>);
                }
                if (row.dumpType === 'CUSTOMER_PORTAL_REPORT') {
                    return renderRemittanceFilters(text as Record<string, string>);
                }
                return renderFilters(text as Record<string, string>);
            case 'serialNumber': return renderText(index + 1);
            case 'url': return renderDownload(text as string);
            case 'createdAt': return renderDate(text as Moment);
            case 'dumpType':
            case 'status':
            default: return renderText(text as string);
        }
    };

    const getWidth = (column: string) => {
        switch (column) {
            case 'filterParams': return 300;
            case 'url':
            case 'dumpType':
            case 'status':
            case 'serialNumber':
            default: return 150;
        }
    };

    const getColumns = (): ColumnProps<Request>[] => {
        const columns: ColumnProps<Request>[] = Object.keys(downloadColumns).map((column) => {
            return {
                key: column,
                title: t(column),
                dataIndex: column,
                ellipsis: true,
                width: getWidth(column),
                render: (
                    text: string | Record<string, string> | Moment,
                    row: Request,
                    index: number,
                ) => renderColumn(text, row, column, index),
            };
        });
        return columns;
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                bordered={false}
                pagination={false}
                loading={loading}
                rowKey={(row: Request) => JSON.stringify(row)}
                columns={getColumns()}
                scroll={{
                    y: 'calc(100% - 125px)',
                    x: true,
                }}
                style={{
                    height: 'calc(100% - 80px)',
                    background: '#ffffff',
                }}
                dataSource={requests}
            />
        );
    };

    return (
        <div className={classes.main} style={{ height: '100%' }}>
            {renderTitle()}
            {renderTable()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    return {
        master,
    };
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: downloadPageStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

const DownloadPage = GenericHoc(hocConfig)(Downloads);
export default DownloadPage;
